import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const [isMobileLoginOpen, setIsMobileLoginOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Update active section
      const sections = document.querySelectorAll('section[id]');
      let current = '';
      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 100) {
          current = section.getAttribute('id');
        }
      });
      setActiveSection(current);
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLoginDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navLinks = [
    { name: 'Home', href: 'https://entrepreneurladyhustler.com/en/about-us/', external: true },
    { name: 'About', href: '#about' },
    { name: 'What We Do', href: '#what-we-do' },
    { name: 'Membership', href: '#membership' },
    { name: 'Get Involved', href: '#get-involved' },
    { name: 'The Journal', href: 'https://blog.entrepreneurladyhustler.com/en/blog', external: true },
    { name: 'Contact', href: 'mailto:hello@entrepreneurladyhustler.com', external: true }
  ];

  const loginOptions = [
    { name: 'Membership Login', href: 'https://app.entrepreneurladyhustler.com/login' }
  ];

  const scrollToSection = (e, href, external) => {
    if (external) {
      return; // Let the default link behavior work
    }
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[var(--white-soft)] shadow-sm border-b border-[var(--pink-dusty)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#hero" onClick={(e) => scrollToSection(e, '#hero')} className="flex items-center space-x-3">
              <img 
                src="/assets/lhsc-logo.png" 
                alt="The Lady Hustler Society Club - Female Entrepreneurs Association Logo - Women's Professional Development" 
                className="h-12 w-auto"
                style={{ objectFit: 'contain' }}
                loading="eager"
              />
              <div className="hidden lg:block">
                <div className="text-xs font-semibold tracking-widest lhsc-heading" style={{ fontSize: '0.7rem' }}>
                  THE LADY HUSTLER SOCIETY CLUB
                </div>
              </div>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center">
              {navLinks.map((link, index) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href, link.external)}
                  className={`text-xs uppercase transition-colors ${index > 0 ? 'ml-8' : ''}`}
                  style={{
                    fontFamily: 'Josefin Sans, sans-serif',
                    fontWeight: 400,
                    color: activeSection === link.href.substring(1) ? 'var(--mauve-rose)' : 'var(--text-dark)',
                    textDecoration: 'none'
                  }}
                  {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {link.name}
                </a>
              ))}
              
              {/* Member Login Dropdown */}
              <div className="relative ml-8" ref={dropdownRef}>
                <button
                  onClick={() => setIsLoginDropdownOpen(!isLoginDropdownOpen)}
                  className="text-xs uppercase transition-colors flex items-center space-x-1"
                  style={{
                    fontFamily: 'Josefin Sans, sans-serif',
                    fontWeight: 400,
                    color: 'var(--text-dark)',
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <span>Member Login</span>
                  <ChevronDown size={14} style={{ 
                    transition: 'transform 0.2s',
                    transform: isLoginDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                  }} />
                </button>
                
                {/* Dropdown Menu */}
                {isLoginDropdownOpen && (
                  <div 
                    className="absolute right-0 mt-2 w-56 rounded-md shadow-lg py-2 z-50"
                    style={{ 
                      backgroundColor: 'var(--white-soft)',
                      border: '1px solid var(--pink-dusty)'
                    }}
                  >
                    {loginOptions.map((option) => (
                      <a
                        key={option.name}
                        href={option.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-3 text-sm uppercase hover:bg-[var(--pink-blush)] transition-colors"
                        style={{
                          fontFamily: 'Josefin Sans, sans-serif',
                          fontWeight: 400,
                          color: 'var(--text-dark)',
                          textDecoration: 'none'
                        }}
                        onClick={() => setIsLoginDropdownOpen(false)}
                      >
                        {option.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <a
                href="https://entrepreneurladyhustler.com/en/become-a-lady-hustler/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-md text-white font-medium text-sm whitespace-nowrap"
                style={{ 
                  backgroundColor: 'var(--mauve-rose)', 
                  fontFamily: 'Josefin Sans, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 500
                }}
              >
                Become a Lady Hustler
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2"
              style={{ color: 'var(--mauve-rose)' }}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden overflow-y-auto"
          style={{ backgroundColor: 'var(--pink-blush)' }}
        >
          <div className="flex flex-col items-center justify-center min-h-full py-20 px-6 space-y-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href, link.external)}
                className="text-2xl sm:text-3xl lhsc-heading text-center"
                style={{ color: 'var(--text-dark)' }}
                {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {link.name}
              </a>
            ))}
            
            {/* Mobile Member Login Section */}
            <div className="w-full max-w-xs">
              <button
                onClick={() => setIsMobileLoginOpen(!isMobileLoginOpen)}
                className="text-2xl sm:text-3xl lhsc-heading text-center w-full flex items-center justify-center space-x-2"
                style={{ 
                  color: 'var(--text-dark)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <span>Member Login</span>
                <ChevronDown size={24} style={{ 
                  transition: 'transform 0.2s',
                  transform: isMobileLoginOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }} />
              </button>
              
              {isMobileLoginOpen && (
                <div className="mt-4 space-y-3 w-full">
                  {loginOptions.map((option) => (
                    <a
                      key={option.name}
                      href={option.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-3 px-4 rounded-md text-center text-base lhsc-subheading"
                      style={{ 
                        backgroundColor: 'var(--white-soft)',
                        color: 'var(--text-dark)',
                        border: '1px solid var(--pink-dusty)',
                        textDecoration: 'none'
                      }}
                    >
                      {option.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            
            <a
              href="https://entrepreneurladyhustler.com/en/become-a-lady-hustler/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 sm:px-8 sm:py-4 rounded-md text-white font-semibold text-sm sm:text-base tracking-wider mt-4"
              style={{ 
                backgroundColor: 'var(--mauve-rose)',
                fontFamily: 'Josefin Sans, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 500
              }}
            >
              Become a Lady Hustler
            </a>
            <div className="flex items-center space-x-4 text-sm lhsc-subheading" style={{ color: 'var(--slate-grey)' }}>
              <span>EN</span>
              <span>|</span>
              <span>FR</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
